import React, { useRef, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import i18n from '../../i18n';
// Chart option objects are built in plain helper functions and default
// parameters, where React hooks cannot be called — so translate through the
// i18n instance directly. Charts re-render when their page re-renders on a
// language change, so labels stay in sync.
const t = (key, opts) => i18n.t(key, opts);
// Series name is compared against in the tooltip formatter — single source.
const AVG_WEIGHT_SERIES = () => t('charts:tooltip.avgWeight');

const HeatStressBubbleChart = ({
  data,
  batchNames = {},
  title = null
}) => {
  // default resolved in-body: the hook is not in scope in the parameter list
  const chartTitle = title ?? t('charts:titles.heatStress');
  const chartRef = useRef(null);

  // Force canvas resize after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance) {
        instance.resize();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [data]);

  // No data placeholder
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "500px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          border: "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            writingMode: "vertical-rl",
            fontSize: "24px",
            fontWeight: 600,
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: "3px",
          }}
        >
          {t('charts:common.noInformationFound')}
        </div>
      </div>
    );
  }

  // Sort by batchID for consistent display
  const sorted = [...data].sort((a, b) => a.batchID - b.batchID);

  // Prepare data arrays
  const xAxisLabels = [];
  const broodingData = [];
  const dayData = [];
  const nightData = [];
  const weightCircleData = [];

  sorted.forEach((batch, index) => {
    const batchName = batchNames[batch.batchID] || `Batch ${batch.batchID}`;
    xAxisLabels.push(batchName);

    const broodingVal = batch.BroodingMeanTHI_West || 0;
    const dayVal = batch.MeanDayTHI_West || 0;
    const nightVal = batch.MeanNightTHI_West || 0;

    broodingData.push(broodingVal);
    dayData.push(dayVal);
    nightData.push(nightVal);

    // Position circles at a fixed height (bottom of chart area)
    const avgWeight = batch.AvgFinalWeight;
    // Always add circle, even if weight is 0 or undefined (will show N/A)
    weightCircleData.push([index, 40, avgWeight, batchName]);
  });

  // Calculate Y-axis max
  const allValues = [...broodingData, ...dayData, ...nightData].filter(v => v > 0);
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 90;
  const yMax = Math.ceil(maxValue / 10) * 10 + 10;

  // Custom scale transformation to expand 82-90 range
  // Maps actual values to visual positions with expanded spacing only in 82-90 range
  const transformY = (value) => {
    if (value < 82) {
      // Below 82: normal scale (0-82 maps to 0-82)
      return value;
    } else if (value <= 90) {
      // 82-90 range: expand by 4x (8 units becomes 32 units)
      // This gives much more visual space for the threshold labels
      return 82 + (value - 82) * 4;
    } else {
      // Above 90: normal scale again
      return 114 + (value - 90);
    }
  };

  // Inverse of transformY: map a visual (transformed) position back to the real THI value.
  // Used by axis labels, bar labels and the tooltip so they all show actual THI.
  const inverseTransformY = (visualValue) => {
    if (visualValue <= 82) return visualValue;
    if (visualValue <= 114) return 82 + (visualValue - 82) / 4;
    return 90 + (visualValue - 114);
  };

  // Transform data for visual expansion
  const broodingDataTransformed = broodingData.map(transformY);
  const dayDataTransformed = dayData.map(transformY);
  const nightDataTransformed = nightData.map(transformY);
  const weightCircleDataTransformed = weightCircleData.map(item => [
    item[0],
    transformY(item[1]),
    item[2],
    item[3]
  ]);

  const visualYMax = transformY(yMax);

  const option = {
    title: {
      text: chartTitle,
      left: "center",
      top: 10,
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#333",
      },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: "rgba(50,50,50,0.9)",
      borderWidth: 0,
      textStyle: {
        color: "#fff",
        fontSize: 12,
      },
      formatter: (params) => {
        // Filter out the weight circle series from axis tooltip
        const barSeries = params.filter(p => p.seriesName !== AVG_WEIGHT_SERIES());
        const weightSeries = params.find(p => p.seriesName === AVG_WEIGHT_SERIES());

        if (barSeries.length === 0) return "";

        let result = `<strong>${barSeries[0].name}</strong><br/>`;
        barSeries.forEach(item => {
          // Series data holds transformed positions — convert back to real THI.
          const actual = inverseTransformY(item.value);
          result += `${item.marker} ${item.seriesName}: ${actual.toFixed(1)}<br/>`;
        });

        // Add Avg Final Weight if available
        if (weightSeries) {
          const weight = weightSeries.data[2];
          if (weight && weight !== 0) {
            result += `${weightSeries.marker} ${t('charts:tooltip.avgFinalWeight')}: ${weight.toFixed(2)} Kg<br/>`;
          } else {
            result += `${weightSeries.marker} Avg Final Weight: N/A<br/>`;
          }
        }

        return result;
      },
    },
    legend: {
      data: [t('charts:heat.broodingThi'), t('charts:heat.dayThi'), t('charts:heat.nightThi')],
      top: 45,
      left: "center",
      orient: "horizontal",
      textStyle: {
        fontSize: 12,
        color: "#333",
      },
    },
    grid: {
      left: "5%",
      right: "10%",
      top: "20%",
      bottom: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: xAxisLabels,
      name: t('charts:common.batch'),
      nameLocation: "middle",
      nameGap: 35,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#333",
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#333",
          width: 2,
        },
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
      },
      axisLabel: {
        rotate: 0,
        interval: 0,
        fontSize: 11,
        color: "#333",
      },
    },
    yAxis: {
      type: "value",
      name: t('charts:heat.birdHeatStress'),
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#333",
      },
      min: 0,
      max: visualYMax,
      axisLine: {
        show: true,
        lineStyle: {
          color: "#333",
          width: 2,
        },
      },
      axisTick: {
        show: true,
      },
      axisLabel: {
        fontSize: 11,
        color: "#333",
        formatter: (visualValue) => {
          // Reverse transform to show actual THI values
          let actualValue;
          if (visualValue <= 82) {
            actualValue = visualValue;
          } else if (visualValue <= 114) {
            actualValue = 82 + (visualValue - 82) / 4;
          } else {
            actualValue = 90 + (visualValue - 114);
          }

          // Show labels at key points
          const rounded = Math.round(actualValue * 10) / 10;
          if (rounded % 20 === 0 || rounded === 82 || rounded === 84 || rounded === 86 || rounded === 90) {
            return Math.round(rounded);
          }
          return '';
        },
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: "#e5e5e5",
          width: 1,
        },
      },
    },
    series: [
      {
        name: t('charts:heat.broodingThi'),
        type: "bar",
        data: broodingDataTransformed,
        barWidth: 25,
        itemStyle: {
          color: "#fac858",
        },
        label: {
          show: true,
          position: "top",
          fontSize: 10,
          color: "#333",
          fontWeight: "bold",
          formatter: (params) => inverseTransformY(params.value).toFixed(1),
        },
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: {
            type: "dashed",
            width: 2,
          },
          label: {
            fontSize: 11,
            fontWeight: "bold",
            position: "end",
          },
          data: [
            {
              yAxis: transformY(82),
              lineStyle: { color: "#27ae60" },
              label: {
                formatter: "Normal",
                color: "#27ae60",
              },
            },
            {
              yAxis: transformY(84),
              lineStyle: { color: "#f39c12" },
              label: {
                formatter: "Moderate",
                color: "#f39c12",
              },
            },
            {
              yAxis: transformY(86),
              lineStyle: { color: "#e67e22" },
              label: {
                formatter: "Severe",
                color: "#e67e22",
              },
            },
            {
              yAxis: transformY(88),
              lineStyle: { color: "#e74c3c" },
              label: {
                formatter: "Critical",
                color: "#e74c3c",
              },
            },
          ],
        },
      },
      {
        name: t('charts:heat.dayThi'),
        type: "bar",
        data: dayDataTransformed,
        barWidth: 25,
        itemStyle: {
          color: "#5cb85c",
        },
        label: {
          show: true,
          position: "top",
          fontSize: 10,
          color: "#333",
          fontWeight: "bold",
          formatter: (params) => inverseTransformY(params.value).toFixed(1),
        },
      },
      {
        name: t('charts:heat.nightThi'),
        type: "bar",
        data: nightDataTransformed,
        barWidth: 25,
        itemStyle: {
          color: "#337ab7",
        },
        label: {
          show: true,
          position: "top",
          fontSize: 10,
          color: "#333",
          fontWeight: "bold",
          formatter: (params) => inverseTransformY(params.value).toFixed(1),
        },
      },
      {
        name: AVG_WEIGHT_SERIES(),
        type: "scatter",
        data: weightCircleDataTransformed,
        symbolSize: 70,
        symbol: "circle",
        z: 10,
        itemStyle: {
          color: "#9467bd",
          borderColor: "#999",
          borderWidth: 2,
          borderType: [5, 5],
        },
        label: {
          show: true,
          formatter: (params) => {
            const weight = params.data[2];
            if (!weight || weight === 0) {
              return `{weight|N/A}`;
            }
            return `{weight|${weight.toFixed(2)}}\n{unit|Kg}`;
          },
          rich: {
            weight: {
              fontSize: 14,
              fontWeight: "bold",
              color: "#fff",
              lineHeight: 18,
            },
            unit: {
              fontSize: 14,
              fontWeight: "bold",
              color: "#fff",
            },
          },
        },
        tooltip: {
          formatter: (params) => {
            const batchName = params.data[3];
            const weight = params.data[2];
            if (!weight || weight === 0) {
              return `<strong>${batchName}</strong><br/>Avg Final Weight: N/A`;
            }
            return `<strong>${batchName}</strong><br/>${t('charts:tooltip.avgFinalWeight')}: ${weight.toFixed(2)} Kg`;
          },
        },
      },
    ],
  };

  // Export helpers
  const handleExportPNG = () => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    const url = inst.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#ffffff",
    });
    const link = document.createElement("a");
    link.href = url;
    link.download = "heat-stress-analytics.png";
    link.click();
  };

  const handleExportPDF = () => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    const url = inst.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#ffffff",
    });
    const w = window.open("");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>{t('charts:heat.title')}</title>
      <style>body{margin:0;display:flex;align-items:center;justify-content:center} img{max-width:100%;}</style>
      </head><body><img src="${url}"/></body></html>`);
    w.document.close();
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {}
    }, 500);
  };

  return (
    <div className="Temp">
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ width: "100%", height: "650px" }}
        opts={{ renderer: "svg" }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "30px",
          marginTop: "10px",
          padding: "15px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "40px",
              height: "2px",
              borderTop: "2px dashed #27ae60",
            }}
          ></div>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#27ae60" }}>
            &lt;82 Normal
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "40px",
              height: "2px",
              borderTop: "2px dashed #f39c12",
            }}
          ></div>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#f39c12" }}>
            82 to 84 Moderate
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "40px",
              height: "2px",
              borderTop: "2px dashed #e67e22",
            }}
          ></div>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#e67e22" }}>
            84 to 86 Severe
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "40px",
              height: "2px",
              borderTop: "2px dashed #e74c3c",
            }}
          ></div>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#e74c3c" }}>
            &gt;86 Critical
          </span>
        </div>
      </div>

      <div
        style={{
          textAlign: "center",
          marginTop: "10px",
          padding: "10px",
          fontSize: "13px",
          color: "#666",
          fontStyle: "italic",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <strong>{t('charts:heat.note')}</strong> {t('charts:heat.dayNightNote')}
      </div>
    </div>
  );
};

export default HeatStressBubbleChart;
